import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiBaseUrl, authHeaders } from "./dispatcher/shared";
import FleetCostSettings from "./dispatcher/FleetCostSettings";
import InfraCostReport from "./dispatcher/InfraCostReport";
import AIUsageDashboard from "./AIUsageDashboard";
import RouteMonitorModal from "../components/RouteMonitorModal";

/**
 * AIVAN Finance — one owner-friendly financial dashboard.
 *
 * Plain language on the surface; the rigor (data source, calculation, coverage limits) lives in each
 * tab's "How we measure this" drawer and in short gray notes under any estimate. This file is a
 * PRESENTATION + LABELING layer only — it reuses the existing finance endpoints and changes no
 * calculation, backend logic, or stored record.
 *
 * Honesty rules kept intact:
 *  - Actual money (from receipts / the books) is never mixed with estimates or opportunities.
 *  - "Possible savings" is a modeled opportunity (a cheaper stop was reachable), NOT money already
 *    saved, and NOT a claim that a recommendation was sent to the driver.
 *  - Fuel "Plan compliance" shows a number ONLY when verified sent recommendations are matched to
 *    actual purchases; until then it reads "Not yet available" (never a modeled/illustrative %).
 *
 * Filter bar defaults to the LAST COMPLETE WEEK (Mon–Sun) and sends ISO dates. Admin / owner only.
 */

const roles = () => { try { return JSON.parse(localStorage.getItem("roles") || "[]"); } catch { return []; } };
const isSuper = () => roles().includes("super_admin");

// ---------- formatting ----------
const money = (v) => (v == null || isNaN(v) ? "—" : `$${Math.round(Number(v)).toLocaleString()}`);
const money2 = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const moneyK = (v) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v); const a = Math.abs(n);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};
const pct = (v) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`);
const num = (v) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString());

// ---------- period math (fleet-week = Mon 00:00 → Sun 23:59; default = last complete week) ----------
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtD = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
const startOfWeekMon = (ref) => { const x = new Date(ref); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x; };
function rangeFor(key) {
  const now = new Date(), thisMon = startOfWeekMon(now); let from, to;
  if (key === "this-week") { from = thisMon; to = new Date(thisMon); to.setDate(to.getDate() + 6); }
  else if (key === "last-week") { to = new Date(thisMon); to.setDate(to.getDate() - 1); from = new Date(to); from.setDate(from.getDate() - 6); }
  else if (key === "last-7") { to = new Date(now); from = new Date(now); from.setDate(from.getDate() - 6); }
  else if (key === "last-30") { to = new Date(now); from = new Date(now); from.setDate(from.getDate() - 29); }
  else if (key === "this-month") { from = new Date(now.getFullYear(), now.getMonth(), 1); to = new Date(now.getFullYear(), now.getMonth() + 1, 0); }
  else if (key === "last-month") { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
  else return null;
  return { from, to };
}
const prevRangeIso = (fromIso, toIso) => {
  const from = new Date(fromIso), to = new Date(toIso);
  const len = Math.round((to - from) / 86400000) + 1;
  const pTo = new Date(from); pTo.setDate(pTo.getDate() - 1);
  const pFrom = new Date(pTo); pFrom.setDate(pFrom.getDate() - (len - 1));
  return { from: isoD(pFrom), to: isoD(pTo) };
};

const PRESETS = [
  ["this-week", "This week"], ["last-week", "Last week"], ["last-7", "Last 7d"],
  ["last-30", "Last 30d"], ["this-month", "This month"], ["last-month", "Last month"], ["custom", "Custom"],
];
const TABS = [
  ["overview", "Summary"], ["profit", "Profit"], ["fuel", "Fuel"], ["tolls", "Tolls"],
  ["miles", "Empty Miles & Unassigned Driving"], ["cost", "Cost Settings"], ["infra", "Software & Services"],
];

// ---------- fetch helper (never let one failure blank the page) ----------
async function GET(path, params) {
  try {
    const r = await axios.get(`${apiBaseUrl}${path}`, { params, ...authHeaders() });
    return { ok: true, data: r.data };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.error || e?.message || "error" };
  }
}

// Short-lived in-memory cache for slow, period-AGNOSTIC calls (infra cost, dispatcher scorecard,
// the 12-week trend). Re-opening the dashboard within the TTL returns instantly instead of re-querying.
// Only successful responses are cached; date-driven calls are never routed here so filters stay live.
const _getCache = new Map();
async function cachedGET(path, params, ttlMs = 600000) {
  const key = path + "?" + JSON.stringify(params || {});
  const hit = _getCache.get(key);
  if (hit && Date.now() - hit.t < ttlMs) return hit.res;
  const res = await GET(path, params);
  if (res.ok) _getCache.set(key, { t: Date.now(), res });
  return res;
}

// Run thunks with bounded concurrency. Default 4: the dashboard's per-tab fan-out (6-8 read-only
// endpoints) runs a few at a time instead of strictly serial, so page load ≈ the slowest couple of
// batches rather than the SUM of every call. Still bounded (not Promise.all) to stay well under the
// per-worker DB-pool ceiling — one admin's dashboard holds at most `limit` connections at once, trivial
// against gunicorn -w6 × pool 15+15. (The old limit=1 made the page wait for all calls end-to-end.)
async function runLimited(thunks, limit = 4) {
  const results = new Array(thunks.length);
  let i = 0;
  async function worker() {
    while (i < thunks.length) {
      const idx = i++;
      results[idx] = await thunks[idx]();
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, thunks.length) }, worker));
  return results;
}

export default function FinanceDashboard() {
  const [tab, setTab] = useState("overview");
  const [preset, setPreset] = useState("last-week");
  const init = rangeFor("last-week");
  const [range, setRange] = useState({ from: isoD(init.from), to: isoD(init.to) });
  const [basis, setBasis] = useState("");
  const [compare, setCompare] = useState(true);

  const resolvedLabel = useMemo(() => {
    const f = new Date(range.from), t = new Date(range.to);
    const span = `${fmtD(f)} – ${fmtD(t)}, ${t.getFullYear()}`;
    const tag = preset === "last-week" ? "Mon–Sun" : preset === "custom" ? "custom" : preset.replace("-", " ");
    return { span, tag };
  }, [range, preset]);

  const choosePreset = (key) => {
    setPreset(key);
    if (key !== "custom") { const r = rangeFor(key); setRange({ from: isoD(r.from), to: isoD(r.to) }); }
  };
  return (
    <div className="fin-scope" style={st.wrap}>
      <style>{FIN_CSS}</style>

      <header style={st.header}>
        <div style={st.headLeft}>
          <div style={st.badge}>$</div>
          <div>
            <div style={st.eyebrow}>AIVAN · Finance</div>
            <h1 style={st.h1}>Finance</h1>
            <div style={st.subtitle}>What you earned, what you spent, and where you could save — one place.</div>
          </div>
        </div>
        <div style={st.periodChip} title={`Reporting period · ${range.from} → ${range.to}`}>
          <span style={st.periodDot} />
          <b>{resolvedLabel.span}</b>
          <span style={st.periodSep}>·</span>
          <span style={st.periodTag}>{resolvedLabel.tag}</span>
        </div>
      </header>

      {/* filter bar */}
      <div style={st.filters}>
        <span style={st.flabel}>Period</span>
        <div style={st.presets}>
          {PRESETS.map(([k, lbl]) => (
            <button key={k} className={"fin-preset" + (preset === k ? " on" : "")} onClick={() => choosePreset(k)}
              style={{ ...st.preset, ...(preset === k ? st.presetActive : {}) }}>{lbl}</button>
          ))}
        </div>
        {preset === "custom" && (
          <div style={st.frange}>
            <input type="date" className="fin-ctl" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} style={st.dateInp} />
            <span style={{ color: "var(--muted)" }}>→</span>
            <input type="date" className="fin-ctl" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} style={st.dateInp} />
          </div>
        )}
        {tab === "fuel" && (
          <>
            <span style={st.fdiv} />
            <span style={st.flabel}>Fuel view</span>
            <select className="fin-ctl" value={basis} onChange={(e) => setBasis(e.target.value)} style={st.sel} title="Which fuel plan the savings estimate is based on">
              <option value="">All</option>
              <option value="sent_plan">Verified sent plans</option>
              <option value="live_plan_modeled">Modeled (on-file plan)</option>
            </select>
          </>
        )}
        <label style={st.toggle}>
          <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} /> Compare to previous
        </label>
        <div style={{ flex: 1 }} />
        <span style={st.mono}>{range.from} → {range.to}</span>
      </div>

      {/* tabs — full-width segmented bar */}
      <div style={st.tabs}>
        {TABS.map(([k, lbl], i) => (
          <button key={k} className={"fin-tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}
            style={{ ...st.tab, ...(i > 0 ? st.tabDiv : {}), ...(tab === k ? st.tabActive : {}) }}>{lbl}</button>
        ))}
      </div>

      {tab === "overview" && <OverviewPanel range={range} basis={basis} compare={compare} goFuel={() => setTab("fuel")} />}
      {tab === "profit" && <ProfitPanel range={range} />}
      {tab === "fuel" && <FuelPanel range={range} basis={basis} />}
      {tab === "tolls" && <TollsPanel range={range} />}
      {tab === "miles" && <MilesPanel range={range} />}
      {tab === "cost" && <Embed title="Cost settings — the inputs behind every number here (admin-editable)"><FleetCostSettings /></Embed>}
      {tab === "infra" && (
        <Embed title="Software & services — monthly spend (EUR)">
          <InfraCostReport />
          {isSuper() && <div style={{ marginTop: 24 }}><AIUsageDashboard /></div>}
        </Embed>
      )}

      <footer style={st.footer}>
        One place for your fleet's money. <b>Actual</b> costs (from receipts and the books), <b>estimates</b>,
        and <b>verified results</b> are labeled and never mixed. Estimates are opportunities — not money
        already saved. Open <b>“How we measure this”</b> on any tab for the exact source and calculation.
        Admin / owner only.
      </footer>
    </div>
  );
}

// ============================= SUMMARY =============================
function OverviewPanel({ range, basis, compare, goFuel }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const se = { start: range.from, end: range.to };
    const prev = prevRangeIso(range.from, range.to);
    const [stats, prevStats, trend, fuel, perf, tolls, miles, infra] = await runLimited([
      () => GET("/scripted/fleet/stats-summary", se),
      () => compare ? GET("/scripted/fleet/stats-summary", { start: prev.from, end: prev.to }) : Promise.resolve({ ok: false }),
      () => cachedGET("/scripted/fleet/revenue-profit-trend", { weeks: 12 }),
      () => GET("/fleet/fuel-savings-dashboard", { date_from: range.from, date_to: range.to, ...(basis ? { basis } : {}) }),
      () => cachedGET("/scripted/fleet/dispatcher-performance", {}),
      () => GET("/scripted/fleet/toll-compliance", se),
      () => GET("/fleet/miles-report", se),
      () => cachedGET("/fleet/infra-cost-report", {}),
    ], 1);
    setD({ stats, prevStats, trend, fuel, perf, tolls, miles, infra });
    setLoading(false);
  }, [range.from, range.to, basis, compare]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  const money_ = d.stats.ok ? d.stats.data.money || {} : {};
  const cb = d.stats.ok ? d.stats.data.cost_breakdown || {} : {};
  const prevMoney = d.prevStats.ok ? d.prevStats.data.money || {} : null;
  const revenue = money_.revenue, profit = money_.profit;
  const opCost = revenue != null && profit != null ? revenue - profit : null;
  const margin = revenue ? (profit / revenue) * 100 : null;
  const fuelOv = d.fuel.ok ? d.fuel.data.overview || {} : {};
  const tollC = d.tolls.ok ? d.tolls.data : null;
  const infra = d.infra.ok ? d.infra.data : null;
  // Opportunity total = defensible modeled avoidable only: fuel + tolls. Deadhead is a NORMAL cost
  // (repositioning, unavoidable) so it's never counted as loss. Idle/unassigned driving (no-load
  // miles) IS shown as a dollar loss below — but as an UPPER BOUND, since that bucket can include
  // loads missing from AIVAN. Driver pay excluded; fuel+maintenance only; modeled, not receipts.
  const tollAvoid = tollC?.compliance?.variance_to_plan;
  const milesT = d.miles.ok ? (d.miles.data.totals || {}) : {};
  const util = d.stats.ok ? (d.stats.data.utilization || {}) : {};
  const fmPerMile = util.total_miles ? ((cb.fuel_cost || 0) + (cb.maintenance_cost || 0)) / util.total_miles : null;
  const idleMiles = milesT.idle || 0;
  const idleCost = fmPerMile != null ? idleMiles * fmPerMile : null;
  // one running total the owner tracks each period: fuel + tolls + idle (idle is an upper bound)
  const totalOpp = (fuelOv.modeled_avoidable || 0) + (tollAvoid > 0 ? tollAvoid : 0) + (idleCost || 0);
  const perfDisp = d.perf.ok ? (d.perf.data.dispatchers || []) : [];
  const fleetTrucks = perfDisp.reduce((s, x) => s + ((x.trucks || []).length || 0), 0);
  const fleetNet = perfDisp.reduce((s, x) => s + (x.net_profit || 0), 0);
  const fleetProfitPerTruck = fleetTrucks ? fleetNet / fleetTrucks : null;
  // Fleet revenue per mile — SAME weighted metric as the dispatcher performance module's
  // price_per_mile (total revenue ÷ total miles run, loaded + deadhead), aggregated fleet-wide.
  const fleetRevenue = perfDisp.reduce((s, x) => s + (x.revenue || 0), 0);
  const fleetMilesRun = perfDisp.reduce((s, x) => s + (x.total_miles || 0), 0);
  const fleetRevPerMile = fleetMilesRun ? fleetRevenue / fleetMilesRun : null;

  const delta = (cur, prev) => {
    if (!compare || prev == null || cur == null || !prev) return null;
    const pctChg = ((cur - prev) / Math.abs(prev)) * 100;
    return { up: pctChg >= 0, txt: `${pctChg >= 0 ? "▲" : "▼"} ${Math.abs(pctChg).toFixed(0)}%` };
  };

  // Where the money went: real cost components only. We show the FIXED weekly equipment number
  // (equipment_cost) and deliberately omit other_cost -- it's the per-load equipment slice that the
  // profit math adds back then subtracts (showing both would double-count it on screen).
  const costRows = [
    ["Fuel", cb.fuel_cost, "var(--brand)"],
    ["Driver pay", cb.driver_cost, "#6366f1"],
    ["Equipment (fixed weekly)", cb.equipment_cost, "#0ea5e9"],
    ["Maintenance", cb.maintenance_cost, "#64748b"],
    ["Insurance", cb.insurance_cost, "#8b5cf6"],
    ["Office payroll", cb.office_payroll_cost, "#a855f7"],
    ["Tolls", cb.toll_cost, "#16a34a"],
  ].filter((r) => r[1] != null);
  const costTotal = costRows.reduce((s, r) => s + (r[1] || 0), 0) || 1;

  const topDisp = perfDisp.slice()
    .sort((a, b) => (b.net_profit_per_truck || 0) - (a.net_profit_per_truck || 0)).slice(0, 5);

  const weeks = d.trend.ok ? (d.trend.data.weeks || []) : [];
  const maxRev = Math.max(1, ...weeks.map((w) => w.revenue || 0));
  const lab = (v) => (v == null ? "" : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${Math.round(v)}`);
  const mmdd = (s) => { const d2 = new Date(s); return isNaN(d2) ? String(s).slice(5, 10) : `${d2.getMonth() + 1}/${d2.getDate()}`; };

  // fuel snapshot for the summary strip
  const compAvail = fuelOv.verified_station_adherence_rate != null; // only when verified sent-plan evidence exists

  return (
    <div>
      <div style={st.tiles}>
        <Tile label="Revenue" value={moneyK(revenue)} sub={`${num(money_.loads_count)} loads delivered`}
          info="Money in: revenue from loads delivered in this period. Source: stats-summary." delta={delta(revenue, prevMoney?.revenue)} />
        <Tile label="Costs" value={moneyK(opCost)} sub="all costs this period"
          info="Money out: fuel + driver pay + equipment + maintenance + insurance + office payroll + tolls. = Revenue − Profit." />
        <Tile label="Profit" value={moneyK(profit)} valueColor={(profit || 0) < 0 ? "var(--danger)" : "var(--good, #16a34a)"}
          sub={margin != null ? `${margin.toFixed(1)}% margin` : "—"}
          info="What's left after all costs. Source: stats-summary (the same figure the Reports page uses)." delta={delta(profit, prevMoney?.profit)} />
        <Tile label="Profit per mile" value={money_.profit_per_mile != null ? money2(money_.profit_per_mile) : "—"}
          sub="across delivered loads" info="Profit ÷ miles on delivered loads. Source: stats-summary." delta={delta(money_.profit_per_mile, prevMoney?.profit_per_mile)} />
        <Tile label="Revenue per mile" value={fleetRevPerMile != null ? money2(fleetRevPerMile) : "—"}
          sub="across all miles run" info="Revenue ÷ total miles run (loaded + deadhead), fleet-wide — the same $/mi shown per dispatcher in the performance module. Source: dispatcher-performance." />
        <Tile label="Profit per truck" value={moneyK(fleetProfitPerTruck)}
          sub={fleetTrucks ? `${num(fleetTrucks)} trucks with loads` : ""}
          info="Total net profit ÷ trucks that ran loads this period. Source: dispatcher-performance." />
      </div>

      <div style={st.c2}>
        <Card title="Revenue vs net profit" hint="last 12 weeks · after all costs">
          {weeks.length === 0 ? <Muted>No trend data.</Muted> : (
            <>
              <div style={st.bars}>
                {weeks.map((w, i) => (
                  <div key={i} style={st.barGroup} title={`${w.week_start}: revenue ${money(w.revenue)}, net profit ${money(w.net_profit)}`}>
                    <div style={st.barPair}>
                      <div style={st.barCol}><span style={st.barVal}>{lab(w.revenue)}</span>
                        <i style={{ height: `${Math.round((w.revenue || 0) / maxRev * 100)}%`, background: "var(--brand)", opacity: 0.85, width: "100%", borderRadius: "3px 3px 0 0" }} /></div>
                      <div style={st.barCol}><span style={{ ...st.barVal, color: (w.net_profit || 0) < 0 ? "var(--danger)" : "#16a34a" }}>{lab(w.net_profit)}</span>
                        <i style={{ height: `${Math.max(0, Math.round((w.net_profit || 0) / maxRev * 100))}%`, background: (w.net_profit || 0) < 0 ? "var(--danger)" : "#16a34a", width: "100%", borderRadius: "3px 3px 0 0" }} /></div>
                    </div>
                    <span style={st.barDate}>{mmdd(w.week_start)}</span>
                  </div>
                ))}
              </div>
              <div style={st.legend}><span><i style={{ background: "var(--brand)" }} />Revenue</span><span><i style={{ background: "#16a34a" }} />Net profit</span><span style={{ color: "var(--muted)" }}>$k · week starting</span></div>
              <div style={st.note}>Net profit is after all costs — the same basis as the <b>Profit</b> tile above (fuel, driver, tolls, insurance, payroll, maintenance, and fixed weekly equipment).</div>
            </>
          )}
        </Card>
        <Card title="Where the money went">
          {costRows.length === 0 ? <Muted>No cost breakdown.</Muted> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {costRows.map(([lbl, val, color]) => (
                <div key={lbl} style={st.brow}>
                  <span>{lbl}</span>
                  <div style={st.track}><i style={{ display: "block", height: "100%", borderRadius: 5, width: `${((val || 0) / costTotal) * 100}%`, background: color }} /></div>
                  <span style={st.rnum}>{moneyK(val)}</span>
                </div>
              ))}
            </div>
          )}
          <div style={st.note}>Equipment is a fixed weekly cost per truck — charged even when a truck sits idle.</div>
        </Card>
      </div>

      {/* FUEL snapshot — your three questions, plain */}
      <SectionLabel>Fuel at a glance</SectionLabel>
      <div style={st.three}>
        <MiniCard k="What you paid" v={money(fuelOv.actual_paid_fuel)} note="diesel, from fuel-card receipts" />
        <MiniCard k="Possible savings" v={money2(fuelOv.modeled_avoidable)} tone="warn"
          note="estimate — cheaper diesel was reachable on-route. Not money already saved." />
        <MiniCard k="Plan compliance" v={compAvail ? pct(fuelOv.verified_station_adherence_rate) : "Not yet available"}
          tone={compAvail ? undefined : "muted"} big={compAvail}
          note={compAvail ? "recommended stops the drivers used" : "needs sent recommendations matched to purchases"} />
      </div>
      <div style={{ textAlign: "right", marginTop: 8 }}><button onClick={goFuel} style={st.linkBtn}>Open Fuel ›</button></div>

      <SectionLabel>Money left on the table <span style={st.labelSoft}>(estimated — fuel + tolls + idle, tracked each period)</span></SectionLabel>
      <div style={st.tiles4}>
        <MiniCard k="Fuel — cheaper diesel" v={money2(fuelOv.modeled_avoidable)} tone="warn" note="reachable cheaper stop, on-route" />
        <MiniCard k="Tolls — off planned route" v={tollAvoid != null ? money(tollAvoid) : "—"} tone="warn" note="vs the planned route · reference prices" />
        <MiniCard k="Idle / unassigned driving" v={money(idleCost)} tone="warn"
          note={`${num(Math.round(idleMiles))} no-load mi × ${fmPerMile != null ? money2(fmPerMile) : "—"}/mi · upper bound`} />
        <MiniCard k="Total" v={money(totalOpp)} tone="accent" big note="fuel + tolls + idle" />
      </div>
      <div style={st.note}>Idle/unassigned is an upper bound — it can include loads not yet entered in AIVAN; entering every load tightens it. Deadhead (normal repositioning) is a cost, not counted here.</div>

      <div style={st.c2}>
        <Card title="Top dispatchers" hint="profit per truck · this period">
          {topDisp.length === 0 ? <Muted>No dispatcher data.</Muted> : (
            <Table head={["Dispatcher", "Profit/truck", "$/mi", "Loads"]} rightFrom={1}>
              {topDisp.map((x) => (
                <tr key={x.dispatcher_id}>
                  <td style={st.td}>{x.dispatcher_name || x.dispatcher_id}</td>
                  <td style={st.tdR}>{money(x.net_profit_per_truck)}</td>
                  <td style={st.tdR}>{money2(x.price_per_mile)}</td>
                  <td style={st.tdR}>{num(x.loads_count)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
        <Card title="Software & services" hint="per month, EUR">
          {!infra || !infra.available ? <Muted>{infra?.message || "No data."}</Muted> : (
            <div style={st.kv}>
              <span>AWS (servers + database)</span><b>€{num(Math.round(infra.aws_total_eur))}</b>
              <span>Other services</span><b>€{num(Math.round(infra.external_total_eur))}</b>
              <span style={st.kvTot}>Total / {infra.period_label || "mo"}</span><b style={st.kvTotV}>€{num(Math.round(infra.grand_total_eur))}</b>
            </div>
          )}
        </Card>
      </div>

      <Methodology items={[
        ["Revenue / Costs / Profit", "From stats-summary — the same company figures the Reports page uses. Profit = revenue − (fuel + driver + tolls + insurance + office payroll + maintenance + fixed weekly equipment). Costs shown = revenue − profit."],
        ["Equipment cost", "A fixed weekly amount per truck (fleet setting), charged to every assigned truck even when idle. Set it under Cost Settings."],
        ["Why a week can look low", "Revenue only counts loads DELIVERED in the period, but fixed costs cover the whole fleet-week. A week with many loads still in transit shows their trucks' costs but not yet their revenue."],
        ["Weekly profit trend", "The trend line is NET profit per week — computed on the same basis as the Profit tile (contribution + per-load equipment add-back − the real fixed weekly equipment cost), so the chart and the tile agree."],
        ["Fuel — paid", "Actual diesel from Relay fuel-card receipts (real invoiced money)."],
        ["Fuel — possible savings", "Modeled: a cheaper station was reachable on-route with comparable same-day prices. An opportunity estimate — not money already saved, and not proof a recommendation was sent to the driver."],
        ["Fuel — plan compliance", "Shows a % only when sent fuel recommendations are matched against actual purchases. Until that evidence exists it reads “Not yet available” — never an illustrative number."],
        ["Tolls / Empty miles", "Toll figures are reference-priced (not invoices). Empty miles are real GPS distance; any cost attached to miles is modeled (MPG is a fixed estimate)."],
      ]} />
      <PartialNote d={d} />
    </div>
  );
}

// ============================= PROFIT =============================
function ProfitPanel({ range }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    let live = true; setLoading(true); setDrill(null);
    const se = { start: range.from, end: range.to };
    runLimited([
      () => GET("/scripted/fleet/truck-cost-report", se),
      () => GET("/scripted/fleet/lane-profitability", { ...se, page: 1, page_size: 50 }),
      () => cachedGET("/scripted/fleet/dispatcher-performance", {}),
    ], 1).then(([truckRep, lanes, perf]) => { if (live) { setD({ truckRep, lanes, perf }); setLoading(false); } });
    return () => { live = false; };
  }, [range.from, range.to]);

  const allLoads = useMemo(() => {
    if (!d?.perf?.ok) return [];
    return (d.perf.data.dispatchers || []).flatMap((dp) =>
      (dp.loads || []).map((l) => ({ ...l, dispatcher_name: dp.dispatcher_name })));
  }, [d]);

  if (loading) return <Loading />;

  const trucks = d.truckRep.ok ? (d.truckRep.data.dispatchers || []).flatMap((dp) =>
    (dp.trucks || []).map((t) => ({ ...t, dispatcher_name: dp.dispatcher_name }))) : [];
  trucks.sort((a, b) => (b.profit || 0) - (a.profit || 0));
  const lanes = d.lanes.ok ? (d.lanes.data.lanes || []) : [];
  const dispatchers = d.perf.ok ? (d.perf.data.dispatchers || []).slice().sort((a, b) => (b.net_profit || 0) - (a.net_profit || 0)) : [];

  const openTruck = (t) => {
    const loads = allLoads.filter((l) => String(l.unit_number) === String(t.unit_number));
    setDrill({ crumb: ["Profit", "Trucks", t.unit_number || t.truck_id], title: `Loads for ${t.unit_number || t.truck_id}`, loads });
  };
  const openDisp = (dp) => {
    const loads = (dp.loads || []).map((l) => ({ ...l, dispatcher_name: dp.dispatcher_name }));
    setDrill({ crumb: ["Profit", "Dispatchers", dp.dispatcher_name || dp.dispatcher_id], title: `Loads booked by ${dp.dispatcher_name || dp.dispatcher_id}`, loads });
  };

  if (drill) {
    return (
      <div>
        <div style={st.crumb}>
          <button onClick={() => setDrill(null)} style={st.crumbBtn}>Profit</button>
          <span style={st.crumbSep}>▸</span><span style={st.crumbSep}>{drill.crumb[1]}</span>
          <span style={st.crumbSep}>▸</span><span style={st.crumbCur}>{drill.crumb[2]}</span>
        </div>
        <Card title={drill.title} hint={`${drill.loads.length} loads · selected period`}>
          {drill.loads.length === 0 ? <Muted>No booked loads for this selection in range.</Muted> : (
            <Table head={["Lane", "Pickup", "Miles", "$/mi", "Rate"]} rightFrom={2}>
              {drill.loads.map((l, i) => (
                <tr key={l.load_id || i}>
                  <td style={st.td}>{l.origin} → {l.destination}</td>
                  <td style={st.td}>{l.pickup_datetime ? String(l.pickup_datetime).slice(0, 10) : "—"}</td>
                  <td style={st.tdR}>{num(l.miles)}</td>
                  <td style={st.tdR}>{money2(l.price_per_mile)}</td>
                  <td style={st.tdR}>{money(l.rate_offered)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div>
      <SectionLabel>By truck · by lane · by dispatcher <span style={st.labelSoft}>— click a truck or dispatcher to see its loads</span></SectionLabel>
      <div style={st.c2}>
        <Card title="Trucks">
          <Table head={["Truck", "Revenue", "Cost", "Profit", "$/mi", ""]} rightFrom={1}>
            {trucks.slice(0, 40).map((t) => (
              <tr key={t.truck_id} onClick={() => openTruck(t)} style={st.rowClick}>
                <td style={st.td}>{t.unit_number || t.truck_id}</td>
                <td style={st.tdR}>{moneyK(t.revenue)}</td>
                <td style={st.tdR}>{moneyK(t.total_cost)}</td>
                <td style={{ ...st.tdR, color: (t.profit || 0) < 0 ? "var(--danger)" : "var(--good, #16a34a)" }}>{moneyK(t.profit)}</td>
                <td style={st.tdR}>{money2(t.profit_per_mile)}</td>
                <td style={st.chev}>›</td>
              </tr>
            ))}
          </Table>
          {trucks.length === 0 && <Muted>No truck data for range.</Muted>}
          <div style={st.note}>A truck can show a loss when it ran few loads — its fixed weekly equipment cost is charged even when idle.</div>
        </Card>
        <Card title="Lanes">
          <Table head={["Lane", "Loads", "Profit/load"]} rightFrom={1}>
            {lanes.slice(0, 40).map((l, i) => (
              <tr key={i}>
                <td style={st.td}>{l.origin} → {l.destination}</td>
                <td style={st.tdR}>{num(l.loads_count)}</td>
                <td style={{ ...st.tdR, color: (l.total_profit || 0) < 0 ? "var(--danger)" : "var(--good, #16a34a)" }}>
                  {money(l.loads_count ? (l.total_profit || 0) / l.loads_count : null)}</td>
              </tr>
            ))}
          </Table>
          {lanes.length === 0 && <Muted>No lane data for range.</Muted>}
        </Card>
      </div>
      <SectionLabel>Dispatchers <span style={st.labelSoft}>— click to see their loads</span></SectionLabel>
      <Card>
        <Table head={["Dispatcher", "Profit", "Profit/truck", "$/mi", "Loads", ""]} rightFrom={1}>
          {dispatchers.map((dp) => (
            <tr key={dp.dispatcher_id} onClick={() => openDisp(dp)} style={st.rowClick}>
              <td style={st.td}>{dp.dispatcher_name || dp.dispatcher_id}</td>
              <td style={{ ...st.tdR, color: (dp.net_profit || 0) < 0 ? "var(--danger)" : undefined }}>{moneyK(dp.net_profit)}</td>
              <td style={st.tdR}>{money(dp.net_profit_per_truck)}</td>
              <td style={st.tdR}>{money2(dp.price_per_mile)}</td>
              <td style={st.tdR}>{num(dp.loads_count)}</td>
              <td style={st.chev}>›</td>
            </tr>
          ))}
        </Table>
        {dispatchers.length === 0 && <Muted>No dispatcher data.</Muted>}
      </Card>
      <Methodology items={[
        ["Truck / lane / dispatcher profit", "From truck-cost-report, lane-profitability and dispatcher-performance. Profit = revenue − trip costs − that truck's share of fixed weekly equipment."],
        ["$/mi spikes", "A dispatcher who booked a couple of short, high-rate loads can show a very high $/mi on few miles — read it alongside the load count."],
      ]} />
    </div>
  );
}

// ============================= FUEL =============================
const FUEL_GAP_LABELS = {
  unresolved_truck_identity: "Couldn't match the truck",
  no_fuel_recommendation: "No fuel recommendation on the load",
  no_captured_sms: "No recorded recommendation sent",
  delivery_unverified_or_unknown: "Delivery not confirmed",
  unverified_station_snapshot: "Unverified station in the sent plan",
  timezone_uncertainty: "Purchase time not yet validated",
  incomparable_or_stale_prices: "Prices not comparable / stale",
  other_insufficient: "Other — not enough evidence",
};

function FuelPanel({ range, basis }) {
  const [scope, setScope] = useState({}); // {dispatcher_id,dispatcher_name,truck_id,truck_unit}
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [routeLoad, setRouteLoad] = useState(null);

  useEffect(() => {
    let live = true; setLoading(true); setErr(null);
    GET("/fleet/fuel-savings-dashboard", {
      date_from: range.from, date_to: range.to, ...(basis ? { basis } : {}),
      ...(scope.dispatcher_id ? { dispatcher_id: scope.dispatcher_id } : {}),
      ...(scope.truck_id ? { truck_id: scope.truck_id } : {}),
    }).then((r) => { if (!live) return; if (r.ok) setD(r.data); else setErr(r.error); setLoading(false); });
    return () => { live = false; };
  }, [range.from, range.to, basis, scope.dispatcher_id, scope.truck_id]);

  if (loading) return <Loading />;
  if (err) return <div style={st.errBox}>Couldn’t load fuel data: {err}</div>;
  const ov = d.overview || {}, sent = d.sent_plan_adherence || {};
  const avgPpg = ov.gallons ? ov.actual_paid_fuel / ov.gallons : null;
  const compAvail = ov.verified_station_adherence_rate != null;
  const level = scope.truck_id ? "loads" : scope.dispatcher_id ? "trucks" : "dispatchers";

  return (
    <div>
      {(scope.dispatcher_id || scope.truck_id) && (
        <div style={st.crumb}>
          <button onClick={() => setScope({})} style={st.crumbBtn}>Fuel</button><span style={st.crumbSep}>▸</span>
          {scope.truck_id
            ? <><button onClick={() => setScope({ dispatcher_id: scope.dispatcher_id, dispatcher_name: scope.dispatcher_name })} style={st.crumbBtn}>{scope.dispatcher_name}</button><span style={st.crumbSep}>▸</span><span style={st.crumbCur}>{scope.truck_unit}</span></>
            : <span style={st.crumbCur}>{scope.dispatcher_name}</span>}
        </div>
      )}

      <div style={st.three}>
        <MiniCard k="What you paid" v={money(ov.actual_paid_fuel)} big note={`diesel · ${num(ov.eligible_purchases)} fill-ups${avgPpg != null ? ` · ${money2(avgPpg)}/gal avg` : ""} · from fuel-card receipts`} />
        <MiniCard k="Possible savings" v={money2(ov.modeled_avoidable)} tone="warn" big note="estimate if drivers had used the cheaper stop that was reachable on-route — not money already saved, and not proof a recommendation was sent." />
        <MiniCard k="Plan compliance" v={compAvail ? pct(ov.verified_station_adherence_rate) : "Not yet available"} tone={compAvail ? undefined : "muted"} big={compAvail}
          note={compAvail ? `${num(sent.stations_followed)}/${num(sent.stations_recommended)} recommended stops used · ${num(sent.deviations)} deviations` : "Shows once sent fuel recommendations are matched to actual purchases (Stage 2). We won't show an illustrative number."} />
      </div>

      <SectionLabel>
        {level === "dispatchers" ? <>By dispatcher <span style={st.labelSoft}>— click to drill into trucks</span></>
          : level === "trucks" ? <>Trucks <span style={st.labelSoft}>— click to see this truck's fuel purchases</span></>
          : <>Fuel purchases <span style={st.labelSoft}>— open the route for any load</span></>}
      </SectionLabel>
      <Card>
        {level === "dispatchers" && (
          <Table head={["Dispatcher", "Fill-ups", "Paid", "Possible savings", ""]} rightFrom={1} rightTo={3}>
            {(d.by_dispatcher || []).map((x) => (
              <tr key={x.dispatcher_id} onClick={() => setScope({ dispatcher_id: x.dispatcher_id, dispatcher_name: x.dispatcher })} style={st.rowClick}>
                <td style={st.td}>{x.dispatcher || x.dispatcher_id}</td>
                <td style={st.tdR}>{num(x.eligible_purchases)}</td>
                <td style={st.tdR}>{money(x.actual_relay_spend)}</td>
                <td style={{ ...st.tdR, color: "var(--warn)" }}>{money2(x.modeled_avoidable)}</td>
                <td style={st.chev}>›</td>
              </tr>
            ))}
            {(d.by_dispatcher || []).length === 0 && <tr><td colSpan={5} style={st.tdEmpty}>No fuel data for this period.</td></tr>}
          </Table>
        )}
        {level === "trucks" && (
          <Table head={["Truck", "Fill-ups", "Paid", "Possible savings", ""]} rightFrom={1} rightTo={3}>
            {(d.by_truck || []).map((x) => (
              <tr key={x.truck_id} onClick={() => setScope({ ...scope, truck_id: x.truck_id, truck_unit: x.truck_unit })} style={st.rowClick}>
                <td style={st.td}>{x.truck_unit || x.truck_id}</td>
                <td style={st.tdR}>{num(x.eligible_purchases)}</td>
                <td style={st.tdR}>{money(x.actual_relay_spend)}</td>
                <td style={{ ...st.tdR, color: "var(--warn)" }}>{money2(x.modeled_avoidable)}</td>
                <td style={st.chev}>›</td>
              </tr>
            ))}
            {(d.by_truck || []).length === 0 && <tr><td colSpan={5} style={st.tdEmpty}>No trucks for this dispatcher in range.</td></tr>}
          </Table>
        )}
        {level === "loads" && (
          <Table head={["Where they fueled", "Cheaper option (modeled)", "Gal", "Price paid", "Cheaper", "Diff/gal", ""]} rightFrom={2} rightTo={5}>
            {(d.purchases || []).map((p) => (
              <tr key={p.line_item_id}>
                <td style={st.td}>{p.actual_station || "—"}</td>
                <td style={st.td}>{p.recommended_station || "—"}</td>
                <td style={st.tdR}>{p.actual_gallons ?? "—"}</td>
                <td style={st.tdR}>{p.actual_net_ppg != null ? `$${Number(p.actual_net_ppg).toFixed(3)}` : "—"}</td>
                <td style={st.tdR}>{p.recommended_ppg_sameday != null ? `$${Number(p.recommended_ppg_sameday).toFixed(3)}` : "—"}</td>
                <td style={{ ...st.tdR, color: (p.price_gap_per_gal || 0) > 0 ? "var(--warn)" : "var(--muted)" }}>{p.price_gap_per_gal != null ? `$${Number(p.price_gap_per_gal).toFixed(3)}` : "—"}</td>
                <td style={st.td}>{p.load_id ? <button onClick={() => setRouteLoad({ loadId: p.load_id, unit: p.truck_unit })} style={st.linkBtn}>Route ›</button> : null}</td>
              </tr>
            ))}
            {(d.purchases || []).length === 0 && <tr><td colSpan={7} style={st.tdEmpty}>No fuel purchases for this truck in range.</td></tr>}
          </Table>
        )}
        <div style={st.note}>“Cheaper option” is the lower-priced stop our model found that was reachable on-route — not a record it was recommended to the driver. Click <b>Route</b> on a load to see that trip's route history.</div>
      </Card>

      <Methodology items={[
        ["What you paid", "Actual diesel spend from Relay fuel-card receipts (real invoiced money)."],
        ["Possible savings", "Modeled: a cheaper station was reachable on-route with comparable same-day prices. An opportunity estimate — not realized savings, and not proof a recommendation was sent."],
        ["Plan compliance", "Only counts VERIFIED sent recommendations matched against actual purchases. With none yet captured, it reads “Not yet available” rather than a modeled percentage."],
        ["Coverage", `We could evaluate ${num(ov.comparable_purchases)} of ${num(ov.eligible_purchases)} purchases in this view; the rest are missing a recommendation, a truck match, or a comparable price. Relay fuel-card history is about 10 days deep.`],
        ...(Object.keys(d.evidence_breakdown || {}).length ? [["Why some can't be scored",
          Object.entries(d.evidence_breakdown).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${v} — ${FUEL_GAP_LABELS[k] || k.replace(/_/g, " ")}`).join(" · ")]] : []),
      ]} />
      {routeLoad && <RouteMonitorModal loadId={routeLoad.loadId} unitNumber={routeLoad.unit} onClose={() => setRouteLoad(null)} />}
    </div>
  );
}

// ============================= TOLLS =============================
function TollsPanel({ range }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispName, setDispName] = useState(null);
  const [truck, setTruck] = useState(null);
  const [routeLoad, setRouteLoad] = useState(null);
  const [highView, setHighView] = useState(false);
  useEffect(() => {
    let live = true; setLoading(true); setDispName(null); setTruck(null); setHighView(false);
    GET("/scripted/fleet/toll-compliance", { start: range.from, end: range.to, page_size: 200 }).then((r) => { if (live) { setD(r.ok ? r.data : {}); setLoading(false); } });
    return () => { live = false; };
  }, [range.from, range.to]);

  const groups = useMemo(() => {
    const by = {};
    for (const t of (d?.trucks || [])) {
      const k = t.dispatcher_name || "Unassigned";
      (by[k] = by[k] || { name: k, trucks: [], total: 0, variance: 0, passes: 0 });
      by[k].trucks.push(t); by[k].total += t.total || 0; by[k].variance += t.variance || 0; by[k].passes += t.pass_count || 0;
    }
    return Object.values(by).sort((a, b) => (b.variance || 0) - (a.variance || 0));
  }, [d]);

  // flag every toll pass >= $100 — one row per pass, with who drove it
  const HIGH_TOLL = 100;
  const highTolls = useMemo(() => {
    const out = [];
    for (const t of (d?.trucks || [])) {
      for (const l of (t.loads || [])) {
        for (const p of (l.passes || [])) {
          if ((p.price || 0) >= HIGH_TOLL) out.push({
            driver: t.driver_name || "—", unit: t.unit_number, dispatcher: t.dispatcher_name || "—",
            plaza: p.name || "toll", price: p.price, at: p.recorded_at, lane: l.lane, source: p.source, load_id: l.load_id,
          });
        }
      }
    }
    return out.sort((a, b) => (b.price || 0) - (a.price || 0));
  }, [d]);
  const highDrivers = useMemo(() => new Set(highTolls.map((h) => h.driver)).size, [highTolls]);

  if (loading) return <Loading />;
  const cost = d.cost || {}, comp = d.compliance || {};
  const group = dispName ? groups.find((g) => g.name === dispName) : null;

  return (
    <div>
      <div style={st.tiles4}>
        <Tile label="What you paid (tolls)" value={money(cost.company_actual)} sub="tolls tied to a load"
          info="Toll passes detected on your trucks and tied to a load. Reference-priced from toll locations — not an invoice." />
        <Tile label="Avoidable — off planned route" value={comp.variance_to_plan != null ? money(comp.variance_to_plan) : "—"} valueColor="var(--warn)"
          sub={`estimate · across ${num(comp.comparable_loads)} loads w/ tolls`}
          info="Extra toll cost on routes that differed from the plan, compared only across loads that had tolls on BOTH the plan and the road (toll-free loads excluded). Reference prices — not an invoice." />
        <Tile label="Tied to a load" value={cost.attribution_coverage_pct != null ? pct(cost.attribution_coverage_pct) : "—"} sub="of detected passes"
          info="Share of detected toll passes we could tie to a specific load." />
        <Tile label="Not tied to a load yet" value={money(cost.unattributed)} sub="unassigned toll cost"
          info="Toll passes we detected but couldn't yet tie to a load." />
      </div>

      {highTolls.length > 0 && (
        <>
          <div onClick={() => setHighView((v) => !v)} style={st.flagCard}>
            <span>⚠ <b>{highDrivers}</b> driver{highDrivers === 1 ? "" : "s"} passed a toll of <b>$100+</b> · {highTolls.length} pass{highTolls.length === 1 ? "" : "es"}</span>
            <span style={st.flagLink}>{highView ? "Hide ▴" : "Review ›"}</span>
          </div>
          {highView && (
            <Card>
              <Table head={["Driver", "Truck", "Dispatcher", "Toll plaza", "Toll", "When", "Lane", ""]} rightFrom={4} rightTo={4}>
                {highTolls.map((h, i) => (
                  <tr key={i}>
                    <td style={st.td}>{h.driver}</td>
                    <td style={st.td}>{h.unit}</td>
                    <td style={st.td}>{h.dispatcher}</td>
                    <td style={st.td}>{h.plaza}</td>
                    <td style={{ ...st.tdR, color: "var(--warn)", fontWeight: 700 }}>{money(h.price)}</td>
                    <td style={st.td}>{h.at ? String(h.at).replace("T", " ").slice(0, 16) : "—"}</td>
                    <td style={st.td}>{h.lane || "—"}</td>
                    <td style={st.td}>{h.load_id ? <button onClick={() => setRouteLoad({ loadId: h.load_id, unit: h.unit })} style={st.linkBtn}>Route ›</button> : null}</td>
                  </tr>
                ))}
              </Table>
              <div style={st.note}>Flags any single detected toll pass of $100 or more (big bridges/turnpikes — or a data check). Reference-priced, not an invoice. Click <b>Route</b> to see where it happened.</div>
            </Card>
          )}
        </>
      )}

      {(dispName || truck) && (
        <div style={{ ...st.crumb, marginTop: 20 }}>
          <button onClick={() => { setDispName(null); setTruck(null); }} style={st.crumbBtn}>Tolls</button><span style={st.crumbSep}>▸</span>
          {truck
            ? <><button onClick={() => setTruck(null)} style={st.crumbBtn}>{dispName}</button><span style={st.crumbSep}>▸</span><span style={st.crumbCur}>{truck.unit_number}</span></>
            : <span style={st.crumbCur}>{dispName}</span>}
        </div>
      )}

      <SectionLabel>
        {!dispName ? <>By dispatcher <span style={st.labelSoft}>— click to drill into trucks</span></>
          : !truck ? <>Trucks <span style={st.labelSoft}>— click to see this truck's loads</span></>
          : <>Loads <span style={st.labelSoft}>— open the route for any load</span></>}
      </SectionLabel>
      <Card>
        {!dispName && (
          <Table head={["Dispatcher", "Trucks", "Passes", "Paid", "Variance", ""]} rightFrom={1} rightTo={4}>
            {groups.map((g) => (
              <tr key={g.name} onClick={() => setDispName(g.name)} style={st.rowClick}>
                <td style={st.td}>{g.name}</td>
                <td style={st.tdR}>{num(g.trucks.length)}</td>
                <td style={st.tdR}>{num(g.passes)}</td>
                <td style={st.tdR}>{money(g.total)}</td>
                <td style={{ ...st.tdR, color: (g.variance || 0) > 0 ? "var(--warn)" : "var(--muted)" }}>{money(g.variance)}</td>
                <td style={st.chev}>›</td>
              </tr>
            ))}
            {groups.length === 0 && <tr><td colSpan={6} style={st.tdEmpty}>No toll passes for this period.</td></tr>}
          </Table>
        )}
        {dispName && !truck && (
          <Table head={["Truck", "Driver", "Passes", "Paid", "Expected (plan)", "Variance", ""]} rightFrom={2} rightTo={5}>
            {(group?.trucks || []).map((t, i) => (
              <tr key={i} onClick={() => setTruck(t)} style={st.rowClick}>
                <td style={st.td}>{t.unit_number}</td>
                <td style={st.td}>{t.driver_name || "—"}</td>
                <td style={st.tdR}>{num(t.pass_count)}</td>
                <td style={st.tdR}>{money(t.total)}</td>
                <td style={st.tdR}>{money(t.estimated_total)}</td>
                <td style={{ ...st.tdR, color: (t.variance || 0) > 0 ? "var(--warn)" : "var(--muted)" }}>{money(t.variance)}</td>
                <td style={st.chev}>›</td>
              </tr>
            ))}
          </Table>
        )}
        {truck && (
          <Table head={["Lane", "Status", "Passes", "Paid", "Expected", "Variance", ""]} rightFrom={2} rightTo={5}>
            {(truck.loads || []).map((l, i) => (
              <tr key={l.load_id || i}>
                <td style={st.td}>{l.lane}</td>
                <td style={st.td}>{l.status || "—"}</td>
                <td style={st.tdR}>{num((l.passes || []).length)}</td>
                <td style={st.tdR}>{money(l.total)}</td>
                <td style={st.tdR}>{money(l.estimated)}</td>
                <td style={{ ...st.tdR, color: (l.variance || 0) > 0 ? "var(--warn)" : "var(--muted)" }}>{money(l.variance)}</td>
                <td style={st.td}>{l.load_id ? <button onClick={() => setRouteLoad({ loadId: l.load_id, unit: truck.unit_number })} style={st.linkBtn}>Route ›</button> : null}</td>
              </tr>
            ))}
            {(truck.loads || []).length === 0 && <tr><td colSpan={7} style={st.tdEmpty}>No loads with toll passes for this truck.</td></tr>}
          </Table>
        )}
        <div style={st.note}>Toll prices are a reference rate from toll locations, not invoiced charges. "Avoidable" compares the actual route's tolls to the planned route, only across loads that had tolls on both. Click <b>Route</b> on a load to see its route history.</div>
      </Card>
      {routeLoad && <RouteMonitorModal loadId={routeLoad.loadId} unitNumber={routeLoad.unit} onClose={() => setRouteLoad(null)} />}
    </div>
  );
}

// ============================= EMPTY MILES =============================
function MilesPanel({ range }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true; setLoading(true);
    const se = { start: range.from, end: range.to };
    runLimited([
      () => GET("/fleet/miles-report", se),
      () => GET("/scripted/fleet/stats-summary", se),
    ], 1).then(([mr, ss]) => { if (live) { setD({ mr: mr.ok ? mr.data : {}, ss: ss.ok ? ss.data : {} }); setLoading(false); } });
    return () => { live = false; };
  }, [range.from, range.to]);
  if (loading) return <Loading />;
  const t = (d.mr && d.mr.totals) || {};
  const cb = (d.ss && d.ss.cost_breakdown) || {};
  const util = (d.ss && d.ss.utilization) || {};
  // modeled fuel + mileage-variable maintenance per mile (DRIVER PAY EXCLUDED), from planner cost
  // fields — NOT actual Relay receipts. Applied to DEADHEAD miles only (confirmed empty-to-pickup).
  const fmPerMile = util.total_miles ? ((cb.fuel_cost || 0) + (cb.maintenance_cost || 0)) / util.total_miles : null;
  const emptyCost = fmPerMile != null ? (t.deadhead || 0) * fmPerMile : null;
  const idleCost = fmPerMile != null ? (t.idle || 0) * fmPerMile : null;
  const truckEmptyCost = (tr) => (fmPerMile != null ? (tr.deadhead || 0) * fmPerMile : null);
  const truckIdleCost = (tr) => (fmPerMile != null ? (tr.idle || 0) * fmPerMile : null);
  const trucks = ((d.mr && d.mr.per_truck) || []).slice()
    .sort((a, b) => (b.deadhead || 0) - (a.deadhead || 0));
  const openMap = (tr) => navigate(`/dispatch/truck-map?truck_id=${encodeURIComponent(tr.truck_id)}&start=${range.from}&end=${range.to}`);

  return (
    <div>
      <SectionLabel>Miles (distance)</SectionLabel>
      <div style={st.tiles4}>
        <Tile label="Loaded miles" value={t.loaded_delivered != null ? `${num(Math.round(t.loaded_delivered))} mi` : "—"} sub="carrying a load"
          info="Distance on delivered loads (chosen-route miles). Source: miles-report." />
        <Tile label="Empty miles (deadhead)" value={t.deadhead != null ? `${num(Math.round(t.deadhead))} mi` : "—"} sub={t.deadhead_pct != null ? `${pct(t.deadhead_pct)} of miles` : "empty drive to a pickup"}
          info="Confirmed empty drive to a pickup, on a load (route deadhead_miles). Source: miles-report." />
        <Tile label="Unassigned driving" value={t.idle != null ? `${num(Math.round(t.idle))} mi` : "—"} sub="not linked to a load"
          info="GPS distance moved while not assigned to a load — the 'money lost' miles. May include loads missing from AIVAN, so its dollar cost is shown as an upper bound." />
        <Tile label="Odometer gap" value={t.driven_gap != null ? `${num(Math.round(t.driven_gap))} mi` : "—"} sub={t.driven_gap_pct_of_odometer != null ? `${pct(t.driven_gap_pct_of_odometer)} unaccounted` : ""}
          info="Physical odometer miles we couldn't attribute to loaded/empty/unassigned." />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 11, marginTop: 14 }}>
        <MiniCard k="Empty driving (deadhead) cost" v={money(emptyCost)} tone="warn" big
          note={`${num(Math.round(t.deadhead || 0))} deadhead mi × ${fmPerMile != null ? money2(fmPerMile) : "—"}/mi (fuel + mileage maintenance). A NORMAL operating cost — deadhead is unavoidable to reposition. Driver pay excluded; modeled, not receipts.`} />
        <MiniCard k="Idle / unassigned driving cost (lost)" v={money(idleCost)} tone="accent" big
          note={`${num(Math.round(t.idle || 0))} no-load mi × ${fmPerMile != null ? money2(fmPerMile) : "—"}/mi. The money lost on driving with no load. UPPER BOUND — may include loads missing from AIVAN.`} />
        <MiniCard k="Potential savings" v="Not yet available" tone="muted"
          note="We don't yet separate realistically-avoidable miles, so no savings figure is claimed. These are costs, not proven savings." />
      </div>

      <SectionLabel>By truck <span style={st.labelSoft}>— open the Truck History Map to audit any truck's week on the map</span></SectionLabel>
      <Card>
        <Table head={["Truck", "Loaded", "Empty (deadhead)", "Unassigned", "Empty cost", "Idle cost (lost)", "Odo gap", ""]} rightFrom={1} rightTo={6}>
          {trucks.slice(0, 80).map((tr) => (
            <tr key={tr.truck_id}>
              <td style={st.td}>{tr.unit_number || tr.truck_id}</td>
              <td style={st.tdR}>{num(Math.round(tr.loaded_delivered || 0))}</td>
              <td style={st.tdR}>{num(Math.round(tr.deadhead || 0))}</td>
              <td style={st.tdR}>{num(Math.round(tr.idle || 0))}</td>
              <td style={{ ...st.tdR, color: "var(--muted)" }}>{money(truckEmptyCost(tr))}</td>
              <td style={{ ...st.tdR, color: "var(--warn)" }}>{money(truckIdleCost(tr))}</td>
              <td style={st.tdR}>{tr.gap != null ? num(Math.round(tr.gap)) : "—"}</td>
              <td style={st.td}><button onClick={() => openMap(tr)} style={st.linkBtn}>Map ›</button></td>
            </tr>
          ))}
          {trucks.length === 0 && <tr><td colSpan={8} style={st.tdEmpty}>No truck miles for this period.</td></tr>}
        </Table>
        <div style={st.note}><b>Empty cost</b> = deadhead mi × modeled (fuel+maintenance)/mi — a normal cost. <b>Idle cost (lost)</b> = no-load mi × the same rate — the real waste, but an upper bound (may include unattributed loads). <b>Map ›</b> opens the Truck History Map for that truck over this period.</div>
      </Card>
      <Methodology items={[
        ["Empty vs unassigned", "Empty miles (deadhead) = confirmed empty drive to a pickup on a load — a normal, unavoidable repositioning cost. Unassigned driving = GPS distance with no load linked — the 'money lost' miles — but it can include loads missing from AIVAN, so its cost is an upper bound."],
        ["Cost per mile", "Both costs use the same rate: modeled (fuel + mileage-variable maintenance) ÷ this period's total miles. Driver pay is EXCLUDED (per-mile/hourly/daily varies by driver). MODELED from planner assumptions (cost-model rates × MPG × price), NOT actual Relay fuel receipts."],
        ["Idle cost is an upper bound", "Idle/unassigned cost = no-load miles × that rate. Because the no-load bucket can include real loads that were never entered or matched in AIVAN, treat this as a ceiling, not a confirmed loss."],
        ["Potential savings", "Not shown — we don't yet separate realistically-avoidable miles. These are costs, not proven savings."],
        ["Odometer gap", "Physical odometer distance minus what we attributed to loaded/empty/unassigned — a coverage check, not a cost."],
        ["Map", "“Map ›” deep-links the Truck History Map to that truck for this dashboard's date range (loaded/empty/unassigned paths + playback)."],
      ]} />
    </div>
  );
}

// ============================= small shared UI =============================
function Tile({ label, value, sub, delta, valueColor, info }) {
  return (
    <div style={st.tile} title={info || undefined}>
      <div style={st.tileLab}>{label}{info && <span style={st.infoDot} title={info}> ⓘ</span>}</div>
      <div style={{ ...st.tileBig, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      <div style={st.tileSub}>
        {sub}{delta && <span style={{ color: delta.up ? "var(--good, #16a34a)" : "var(--danger)", fontWeight: 700, marginLeft: 6 }}>{delta.txt}</span>}
      </div>
    </div>
  );
}
function MiniCard({ k, v, note, tone, big }) {
  const color = tone === "warn" ? "var(--warn)" : tone === "muted" ? "var(--muted)" : tone === "accent" ? "var(--brand)" : "var(--ink)";
  const hl = tone === "accent";
  return (
    <div style={{ ...st.mini, ...(hl ? { borderColor: "var(--brand)", background: "var(--brand-wash, var(--card-2))" } : {}) }}>
      <div style={st.miniK}>{k}</div>
      <div style={{ ...st.miniV, color, ...(big ? {} : tone === "muted" ? { fontSize: 16 } : {}) }}>{v}</div>
      {note && <div style={st.miniNote}>{note}</div>}
    </div>
  );
}
function Card({ title, hint, children }) {
  return (
    <div style={st.card}>
      {title && <div style={st.cardHead}><span>{title}</span>{hint && <span style={st.cardHint}>{hint}</span>}</div>}
      {children}
    </div>
  );
}
function SectionLabel({ children }) { return <div style={st.sectionLabel}>{children}</div>; }
function Muted({ children }) { return <div style={st.muted}>{children}</div>; }
function Loading() { return <div style={{ ...st.muted, padding: 40 }}>Loading…</div>; }
function Table({ head, children, rightFrom = 99, rightTo = 99 }) {
  return (
    <div style={st.scrollx}>
      <table style={st.table}>
        <thead><tr>{head.map((h, i) => <th key={i} style={{ ...st.th, textAlign: i >= rightFrom && i <= rightTo ? "right" : "left" }}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function Methodology({ items }) {
  return (
    <details style={st.method}>
      <summary style={st.methodSummary}>How we measure this</summary>
      <div style={st.methodBody}>
        {items.map((it, i) => (
          <div key={i} style={st.methodRow}><div style={st.methodK}>{it[0]}</div><div style={st.methodV}>{it[1]}</div></div>
        ))}
      </div>
    </details>
  );
}
function Embed({ title, children }) {
  return (
    <div>
      <SectionLabel>{title}</SectionLabel>
      <div style={st.embed}>{children}</div>
    </div>
  );
}
function PartialNote({ d }) {
  const failed = Object.entries(d).filter(([, v]) => v && v.ok === false && v.error);
  if (failed.length === 0) return null;
  return <div style={st.partial}>Some panels couldn’t load ({failed.length}); showing what’s available.</div>;
}

// ============================= styles (dispatch theme tokens) =============================
const FIN_CSS = `
.fin-scope button, .fin-scope select, .fin-scope input { transition: background .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease; }
.fin-scope .fin-tab:hover:not(.on) { background: var(--card-2); color: var(--ink); }
.fin-scope .fin-preset:hover:not(.on) { color: var(--ink); background: color-mix(in srgb, var(--ink) 7%, transparent); }
.fin-scope .fin-reset:hover { background: var(--card-2); color: var(--ink); border-color: color-mix(in srgb, var(--ink) 20%, var(--line)); }
.fin-scope .fin-ctl:hover { border-color: color-mix(in srgb, var(--brand) 45%, var(--line)); }
.fin-scope .fin-ctl:focus-visible, .fin-scope .fin-tab:focus-visible, .fin-scope .fin-preset:focus-visible, .fin-scope .fin-reset:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--brand) 55%, transparent); outline-offset: 1px;
}
`;

const st = {
  wrap: { color: "var(--ink)", fontFamily: "inherit", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "16px 18px", marginBottom: 16, borderRadius: "var(--radius-lg)", border: "1px solid var(--line)", background: "linear-gradient(130deg, color-mix(in srgb, var(--brand) 9%, var(--card)), var(--card) 62%)", boxShadow: "var(--shadow-xs)" },
  headLeft: { display: "flex", alignItems: "center", gap: 14 },
  badge: { width: 42, height: 42, borderRadius: 12, display: "grid", placeItems: "center", fontSize: 22, fontWeight: 800, color: "#fff", background: "linear-gradient(135deg, var(--brand), color-mix(in srgb, var(--brand) 55%, #000))", boxShadow: "var(--shadow-xs)", flexShrink: 0 },
  eyebrow: { fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--brand)", fontWeight: 800 },
  h1: { fontSize: 25, fontWeight: 800, letterSpacing: "-.02em", margin: "1px 0 0" },
  subtitle: { fontSize: 12.5, color: "var(--muted)", marginTop: 3 },
  periodChip: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 999, padding: "7px 14px", boxShadow: "var(--shadow-xs)" },
  periodDot: { width: 7, height: 7, borderRadius: "50%", background: "var(--brand)", flexShrink: 0 },
  periodSep: { color: "var(--line)" },
  periodTag: { color: "var(--muted)", fontSize: 11.5 },
  filters: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "10px 14px", boxShadow: "var(--shadow-xs)", marginBottom: 16 },
  flabel: { fontSize: 10, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700 },
  fdiv: { width: 1, alignSelf: "stretch", background: "var(--line)", margin: "2px 2px" },
  presets: { display: "inline-flex", background: "var(--card-2)", border: "1px solid var(--line)", borderRadius: 10, padding: 3, gap: 2, flexWrap: "wrap" },
  preset: { border: "none", background: "transparent", color: "var(--muted)", font: "inherit", fontSize: 12, fontWeight: 600, padding: "5px 11px", borderRadius: 7, cursor: "pointer" },
  presetActive: { background: "var(--card)", color: "var(--brand)", fontWeight: 700, boxShadow: "var(--shadow-xs)" },
  frange: { display: "flex", alignItems: "center", gap: 6 },
  dateInp: { font: "inherit", fontSize: 12.5, padding: "6px 9px", border: "1px solid var(--line)", borderRadius: 8, background: "var(--card)", color: "var(--ink)" },
  sel: { font: "inherit", fontSize: 12.5, padding: "6px 10px", border: "1px solid var(--line)", borderRadius: 8, background: "var(--card)", color: "var(--ink)", cursor: "pointer" },
  toggle: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--muted)", cursor: "pointer", userSelect: "none" },
  mono: { fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontSize: 11.5, color: "var(--muted)" },
  reset: { border: "1px solid var(--line)", background: "var(--card)", color: "var(--muted)", font: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "6px 12px", borderRadius: 8 },
  tabs: { display: "flex", width: "100%", marginBottom: 18, borderRadius: "var(--radius-lg)", border: "1px solid var(--line)", background: "var(--card)", boxShadow: "var(--shadow-xs)", overflow: "hidden" },
  tab: { flex: "1 1 0", minWidth: 0, minHeight: 46, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", border: "none", background: "transparent", font: "inherit", fontSize: 12.5, fontWeight: 600, color: "var(--muted)", padding: "9px 8px", cursor: "pointer", lineHeight: 1.2 },
  tabDiv: { borderLeft: "1px solid var(--line)" },
  tabActive: { background: "color-mix(in srgb, var(--brand) 12%, var(--card))", color: "var(--brand)", fontWeight: 700, boxShadow: "inset 0 -3px 0 var(--brand)" },
  tiles: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 11 },
  tiles4: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 11 },
  three: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 11 },
  tile: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 14, boxShadow: "var(--shadow-xs)" },
  tileLab: { fontSize: 12, color: "var(--muted)", fontWeight: 600 },
  infoDot: { color: "var(--muted)", cursor: "help", fontSize: 10.5 },
  tileBig: { fontSize: 23, fontWeight: 800, letterSpacing: "-.02em", marginTop: 3, fontVariantNumeric: "tabular-nums" },
  tileSub: { fontSize: 11.5, color: "var(--muted)", marginTop: 3 },
  mini: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 14, boxShadow: "var(--shadow-xs)" },
  miniK: { fontSize: 12.5, color: "var(--muted)", fontWeight: 600 },
  miniV: { fontSize: 24, fontWeight: 800, letterSpacing: "-.02em", marginTop: 4, fontVariantNumeric: "tabular-nums" },
  miniNote: { fontSize: 11.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.45 },
  c2: { display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14, marginTop: 14 },
  card: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 14, boxShadow: "var(--shadow-xs)" },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, fontWeight: 700, marginBottom: 12 },
  cardHint: { fontSize: 11, color: "var(--muted)", fontWeight: 500 },
  sectionLabel: { fontSize: 11, letterSpacing: ".09em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, margin: "26px 0 11px" },
  labelSoft: { textTransform: "none", fontWeight: 500, letterSpacing: 0, color: "var(--muted)" },
  note: { fontSize: 11, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 },
  muted: { fontSize: 13, color: "var(--muted)" },
  bars: { display: "flex", alignItems: "flex-end", gap: 6, height: 150, paddingTop: 6 },
  barGroup: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", minWidth: 0 },
  barPair: { display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 2, flex: 1, width: "100%", minHeight: 0 },
  barCol: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", height: "100%", minWidth: 0 },
  barVal: { fontSize: 8.5, fontWeight: 700, color: "var(--brand)", lineHeight: 1, marginBottom: 1, fontVariantNumeric: "tabular-nums" },
  barDate: { fontSize: 9, color: "var(--muted)", marginTop: 4, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  legend: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: "var(--muted)", marginTop: 8 },
  brow: { display: "grid", gridTemplateColumns: "170px 1fr 70px", gap: 10, alignItems: "center", fontSize: 12.5 },
  track: { height: 9, borderRadius: 5, background: "var(--card-2)", overflow: "hidden" },
  rnum: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  linkBtn: { border: "none", background: "none", color: "var(--brand)", font: "inherit", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  kv: { display: "grid", gridTemplateColumns: "1fr auto", gap: "7px 14px", fontSize: 13, alignItems: "center" },
  kvTot: { borderTop: "1px solid var(--line)", paddingTop: 7, marginTop: 3 },
  kvTotV: { borderTop: "1px solid var(--line)", paddingTop: 7, marginTop: 3, color: "var(--brand)" },
  scrollx: { overflowX: "auto" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 12.5 },
  th: { fontSize: 10.5, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--muted)", background: "var(--card-2)", padding: "8px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  td: { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  tdR: { textAlign: "right", padding: "8px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  chev: { textAlign: "right", padding: "8px 10px", borderBottom: "1px solid var(--line)", color: "var(--muted)", fontWeight: 700 },
  tdEmpty: { textAlign: "center", padding: 24, color: "var(--muted)", borderBottom: "1px solid var(--line)" },
  flagCard: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, margin: "14px 0", padding: "12px 15px", borderRadius: "var(--radius-md)", border: "1px solid var(--warn)", background: "var(--warn-wash, #fef3c7)", color: "var(--warn)", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "var(--shadow-xs)" },
  flagLink: { fontWeight: 700, whiteSpace: "nowrap" },
  rowClick: { cursor: "pointer" },
  crumb: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginBottom: 14, flexWrap: "wrap" },
  crumbBtn: { border: "none", background: "none", font: "inherit", fontSize: 12.5, color: "var(--brand)", fontWeight: 700, cursor: "pointer", padding: 0 },
  crumbSep: { color: "var(--muted)" },
  crumbCur: { color: "var(--ink)", fontWeight: 600 },
  method: { marginTop: 22, border: "1px solid var(--line)", borderRadius: "var(--radius-md)", background: "var(--card-2)", padding: "0 13px" },
  methodSummary: { cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "var(--muted)", padding: "11px 0" },
  methodBody: { display: "flex", flexDirection: "column", gap: 10, padding: "4px 0 14px" },
  methodRow: { display: "grid", gridTemplateColumns: "200px 1fr", gap: 14, fontSize: 12, alignItems: "start" },
  methodK: { fontWeight: 700, color: "var(--ink)" },
  methodV: { color: "var(--muted)", lineHeight: 1.55 },
  embed: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 4, boxShadow: "var(--shadow-xs)" },
  errBox: { border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", padding: 12, borderRadius: 10, fontSize: 13 },
  partial: { marginTop: 18, fontSize: 11.5, color: "var(--muted)" },
  footer: { marginTop: 28, borderTop: "1px solid var(--line)", paddingTop: 14, fontSize: 11.5, color: "var(--muted)", lineHeight: 1.7 },
};
